import { z } from 'zod/v4-mini';

import { Schema, cz } from '@/utils/zodUtils.ts';

export const deviceTypeSchema = z.literal([
  'pump',
  'pen',
  'reader',
  'sensor',
  'manual',
]);
export type DeviceType = z.infer<typeof deviceTypeSchema>;

export type InsulinPumpProfileSegment = {
  id: string;
  start: string;
  end: string;
  basalRate: number;
  insulinToCarbRatio: number;
  sensitivity: number;
  bloodGlucoseThreshold?: number;
  targetBloodGlucose?: number;
};

export type InsulinPumpProfile = {
  id: string;
  name: string;
  activeInsulinTime?: number;
  basalInsulinPerDay?: number;
  currentProfile: boolean;

  segments: InsulinPumpProfileSegment[];
};

export type InsulinPump = {
  profiles: InsulinPumpProfile[];
};

export type MedicalDevice = {
  id: string;
  serialNumber?: string;
  name: string;
  manufacturer: string;
  typeId: string;
  kind: DeviceType;
  lastUpload?: string;
  insulinPump?: InsulinPump;
};

export const insulinPumpProfileSegmentSchema: Schema<InsulinPumpProfileSegment> =
  z.pipe(
    cz.camelCaseObject({
      id: cz.id(),
      start_time: z.iso.time(),
      end_time: z.iso.time(),
      basal_rate: z.number(),
      insulin_to_carb_ratio: z.number(),
      sensitivity: z.number(),
      blood_glucose_threshold: cz.optional(z.number()),
      target_blood_glucose: cz.optional(z.number()),
    }),
    z.transform(data => ({
      ...data,
      start: data.startTime,
      end: data.endTime,
    })),
  );

export const insulinPumpProfileSchema: Schema<InsulinPumpProfile> =
  cz.camelCaseObject({
    id: cz.id(),
    name: z.string(),
    active_insulin_time: cz.optional(z.number()),
    basal_insulin_per_day: cz.optional(z.number()),
    current_profile: z.boolean(),
    segments: cz.forcedArray(insulinPumpProfileSegmentSchema),
  });

export const insulinPumpSchema: Schema<InsulinPump> = z.object({
  profiles: cz.forcedArray(insulinPumpProfileSchema),
});

export const medicalDeviceSchema: Schema<MedicalDevice> = z.pipe(
  cz.camelCaseObject({
    id: cz.id(),
    serial_number: cz.optional(z.string()),
    device_type: cz.camelCaseObject({
      id: cz.id(),
      name: z.string(),
      manufacturer: z.string(),
      kind: deviceTypeSchema,
    }),
    last_upload: cz.optional(cz.datetime()),
    insulin_pump: cz.optional(insulinPumpSchema),
  }),
  z.transform(data => ({
    ...data,
    typeId: data.deviceType.id,
    kind: data.deviceType.kind,
    manufacturer: data.deviceType.manufacturer,
    name: data.deviceType.name,
  })),
);
